import posthog from 'posthog-js';

import { SegmentProvider } from './segment';
import { hasAdoptedForwardedAnonymousId } from './segment-anonymous-id';
import { readStoredSegmentUserId } from './stored-user-id';

type AnalyticsParamsOptions = Record<string, unknown>;
type AnalyticsTrackCallback = () => void;

export interface AnalyticsProvider {
  identify(userId?: string, traits?: AnalyticsParamsOptions, options?: AnalyticsParamsOptions): void;
  page(name?: string, properties?: AnalyticsParamsOptions): void;
  reset(): void;
  setAnonymousId(anonymousId: string): void;
  track(
    event: string,
    properties?: AnalyticsParamsOptions,
    options?: AnalyticsParamsOptions,
    callback?: AnalyticsTrackCallback,
  ): void;
}

const segmentProvider = new SegmentProvider();

/**
 * Who this browser was when the snippet handed the session over, captured once per page load.
 *
 * `undefined` until a hand-off page load reaches its first identify; `null` when there was nobody to
 * discard. It bounds the stand-down below to the one occupant the snippet's queued reset covers, so
 * a later identify in the same SPA session — the flag it reads is a `window` property that outlives
 * the hand-off — still goes through the full guard.
 */
let handedOffPredecessorId: string | null | undefined;

function identify(userId?: string, traits?: AnalyticsParamsOptions, options?: AnalyticsParamsOptions): void {
  discardStaleIdentity(userId);

  segmentProvider.identify(userId, traits, options);
  // This is necessary to track feature flag usage
  try {
    posthog.identify(userId, traits);
  } catch (error) {
    console.error('Error identifying user in PostHog', error);
  }
}

/**
 * Forgets a previous occupant of this browser before identifying a new one.
 *
 * Segment keeps `ajs_user_id` in a cookie on the parent domain and clears it only on `reset()`, so
 * a browser that has ever been signed in keeps replaying that id — including on the events sent
 * before the current session is identified. Identifying a *different* account while that id and its
 * anonymous id are still in place binds two users to one anonymous id, and Mixpanel merges two real
 * people into one profile. Mis-attributing a handful of events is a reporting problem; merging
 * profiles is a data-integrity one.
 *
 * The sign-out reset is the first line of defence and this is the second: it covers the sessions
 * that never reach `/sign-out` — expiry, revocation, a device handed to someone else — and every
 * browser that already carries a stale id from before that reset shipped.
 *
 * PostHog is reset alongside Segment here, unlike on `extern-website`. That app runs
 * `persistence: 'memory'` with a bootstrapped `distinctID`, so nothing of its PostHog identity
 * survives a page load; this one uses the default persistence, so a stale identity is just as
 * possible there as in Segment.
 */
function discardStaleIdentity(userId?: string): void {
  if (!userId) {
    return;
  }

  // The library first, storage second. `segmentProvider.userId()` goes through `analytics.user()`,
  // which the snippet's stub does not implement — so while the queue is still buffering it reports
  // nobody even though the id is in the cookie, waiting to be adopted the moment the library loads.
  const storedUserId = segmentProvider.userId() ?? readStoredSegmentUserId();

  if (hasAdoptedForwardedAnonymousId() && handedOffPredecessorId === undefined) {
    handedOffPredecessorId = storedUserId;

    // The snippet reset Segment for this hand-off, and PostHog is the half it could not reach: an
    // inline tag runs long before `posthog.init`, and the previous occupant's person is persisted,
    // so it is still the live one that the `identify` below would write the new account onto.
    //
    // Unconditional, like the snippet's own reset, and for the same reason: the forwarded parameter
    // only ever arrives on the hand-off from extern-website, which by construction carries an
    // account that was just created. Once per page load, because a second reset would land after
    // the identify it is meant to protect and roll away the identity we had just set.
    resetPostHog();
  }

  if (!storedUserId || storedUserId === userId) {
    return;
  }

  // The snippet already settled this occupant: it reset and adopted the id the website forwarded.
  // Both of those are QUEUED, so storage still shows them — and acting on that would queue a second
  // reset that flushes *after* the adoption, rolling a fresh anonymous id and discarding the one the
  // hand-off exists to carry. Leaving the funnel orphaned is exactly the outcome all of this is
  // meant to prevent. Anyone else in storage is staleness the snippet never covered.
  if (storedUserId === handedOffPredecessorId) {
    return;
  }

  // Queued rather than executed while the stub is in place, which is what we want: the library
  // flushes this reset before the identify that follows it, so the order survives the wait.
  segmentProvider.reset();
  resetPostHog();
}

/**
 * Forgets the current PostHog person.
 *
 * `posthog.reset` throws when `init` never ran, which is a normal state here, and a failed flag
 * reset must not stop Segment forgetting the user — that one is the leak that attributes the next
 * visitor's events to the last account.
 */
function resetPostHog(): void {
  try {
    posthog.reset();
  } catch (error) {
    console.error('Error resetting user in PostHog', error);
  }
}

function page(name?: string, properties?: AnalyticsParamsOptions): void {
  segmentProvider.page(name, properties);
}

function track(
  event: string,
  properties?: AnalyticsParamsOptions,
  options?: AnalyticsParamsOptions,
  callback?: AnalyticsTrackCallback,
): void {
  segmentProvider.track(event, properties, options, callback);
}

/** Forgets who this browser was, in both tools. Call it wherever a session ends. */
function reset(): void {
  segmentProvider.reset();
  resetPostHog();
}

function setAnonymousId(anonymousId: string): void {
  segmentProvider.setAnonymousId(anonymousId);
}

export const Analytics = { identify, page, reset, setAnonymousId, track };
